import React from 'react';
import {
  Bell,
  Info,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  X
} from 'lucide-react';

type NotificationType = 'info' | 'warning' | 'alert' | 'success';
export interface Notification {
  id: string;
  message: string;
  type: NotificationType;
}

interface NotificationsComponentProps {
  notifications: Notification[];
}

const NotificationsComponent: React.FC<NotificationsComponentProps> = ({
  notifications
}) => {
  const [dismissedIds, setDismissedIds] = React.useState<Set<string>>(
    new Set()
  );

  const handleDismiss = (id: string) => {
    setDismissedIds((prev) => new Set(prev).add(id));
  };

  const visibleNotifications = notifications.filter(
    (n) => !dismissedIds.has(n.id)
  );

  return (
    <div className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
      <h2 className="text-2xl md:text-3xl font-extrabold mb-6 text-gray-800 flex items-center">
        <div className="bg-yellow-100 p-2 rounded-full mr-3">
          <Bell className="w-6 h-6 text-yellow-600" />
        </div>
        Notifications
        {visibleNotifications.length > 0 && (
          <span className="ml-3 bg-yellow-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
            {visibleNotifications.length}
          </span>
        )}
      </h2>
      <div className="space-y-3">
        {visibleNotifications.length > 0 ? (
          visibleNotifications.map((notification) => {
            let bgColor = '';
            let borderColor = '';
            let iconBgColor = '';
            let icon = null;

            switch (notification.type) {
              case 'info':
                bgColor = 'bg-blue-50';
                borderColor = 'border-blue-200';
                iconBgColor = 'bg-blue-100';
                icon = <Info className="w-5 h-5 text-blue-600" />;
                break;
              case 'warning':
                bgColor = 'bg-yellow-50';
                borderColor = 'border-yellow-200';
                iconBgColor = 'bg-yellow-100';
                icon = <AlertTriangle className="w-5 h-5 text-yellow-600" />;
                break;
              case 'alert':
                bgColor = 'bg-red-50';
                borderColor = 'border-red-200';
                iconBgColor = 'bg-red-100';
                icon = <AlertCircle className="w-5 h-5 text-red-600" />;
                break;
              case 'success':
                bgColor = 'bg-green-50';
                borderColor = 'border-green-200';
                iconBgColor = 'bg-green-100';
                icon = <CheckCircle className="w-5 h-5 text-green-600" />;
                break;
              default:
                bgColor = 'bg-gray-50';
                borderColor = 'border-gray-200';
                iconBgColor = 'bg-gray-100';
                icon = <Bell className="w-5 h-5 text-gray-600" />;
            }

            return (
              <div
                key={notification.id}
                className={`${bgColor} ${borderColor} border-l-4 p-4 rounded-lg flex items-start gap-3 hover:shadow-md transition-all duration-300 group`}
              >
                <div
                  className={`${iconBgColor} p-2 rounded-full flex-shrink-0`}
                >
                  {icon}
                </div>
                <p className="text-gray-700 text-sm md:text-base flex-1 leading-relaxed">
                  {notification.message}
                </p>
                <button
                  onClick={() => handleDismiss(notification.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-gray-400 hover:text-gray-600 flex-shrink-0"
                  aria-label="Dismiss notification"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            );
          })
        ) : (
          <div className="text-center py-8">
            <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
              <Bell className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-500 text-sm">
              You have no new notifications.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsComponent;
